import { config } from '@iheartjane/config';
import type { KioskUser } from '@iheartjane/data-access';
import { AppMode, JaneDM } from '@iheartjane/dm-sdk';
import type { AppMode as EcommAppMode, Nullable } from '@iheartjane/types';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { mapAppModes } from '@jane/search/util';
import { useJaneUser } from '@jane/shared/data-access';
import { useSafeEcommApp } from '@jane/shared/providers';
import { getJaneDeviceId } from '@jane/shared/util';

const dmSdkSourceMonolith = 'monolith';
const dmSdkSourceKiosk = 'kiosk';
const dmSdkSourceBloom = 'bloom';
const t_dmSdkSource = [
  dmSdkSourceMonolith,
  dmSdkSourceKiosk,
  dmSdkSourceBloom,
] as const;

type DmSdkSource = (typeof t_dmSdkSource)[number];

type SDKContextValue = {
  isReady: boolean;
  sdk: JaneDM | null;
};

const SDKContext = createContext<SDKContextValue>({
  isReady: false,
  sdk: null,
});
SDKContext.displayName = 'SDK';

export const useSDK = () => useContext(SDKContext);

const calculateSource = (appMode: EcommAppMode): DmSdkSource => {
  switch (appMode) {
    case AppMode.Bloom:
      return 'bloom';
    case AppMode.Kiosk:
      return 'kiosk';
    default:
      return 'monolith';
  }
};

interface SDKProviderProps {
  appMode?: EcommAppMode;
  jdid?: string;
  kioskUser?: Nullable<KioskUser>;
}

export const SDKProvider = ({
  appMode: appModeProp,
  children,
  jdid: initJdid,
  kioskUser,
}: PropsWithChildren<SDKProviderProps>) => {
  const { dmSdkApiKey, dmEndpoint } = config;
  const ecommApp = useSafeEcommApp();
  const appMode = appModeProp ?? ecommApp.appMode ?? 'default';
  const isKioskMode = appMode === 'kiosk';

  const jdid = initJdid ?? getJaneDeviceId();
  const skipUserFetch = appModeProp !== undefined || isKioskMode;
  const { data: user, isPending } = useJaneUser(undefined, {
    enabled: !skipUserFetch,
  });

  const userId = isKioskMode ? kioskUser?.id : user?.user?.id;
  const distinctId = userId
    ? userId.toString()
    : janeMixpanel.get_distinct_id();

  const identityKey = useMemo(
    () =>
      [jdid ?? '', userId ?? '', distinctId ?? ''].filter(Boolean).join('|'),
    [jdid, userId, distinctId]
  );

  const isReadyToCreateSdk = skipUserFetch || !isPending;

  const sdk = useMemo(() => {
    if (!isReadyToCreateSdk) {
      return { sdk: null, isReady: false };
    }

    return {
      sdk: new JaneDM(
        {
          apiKey: dmSdkApiKey,
          endpoint: dmEndpoint,
          identifier: {
            jdid,
            mixpanelDistinctId: distinctId,
            userId,
          },
        },
        {
          appMode: mapAppModes(appMode),
          source: calculateSource(appMode),
        }
      ),
      isReady: true,
    };
  }, [isReadyToCreateSdk, identityKey]);

  return <SDKContext.Provider value={sdk}>{children}</SDKContext.Provider>;
};
